/** One bank account's history, oldest first, and the evidence of its system signature. */
import { useCallback } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { PartyBankAccountEventType } from '@insio/shared-types';
import {
  PartyBankAccountEventsDocument,
  type PartyBankAccountEventFieldsFragment
} from '@/graphql';
import { parseSystemCheckEvidence, type SystemCheckEvidence } from '../utils/system-check-evidence';

export const BANK_ACCOUNT_EVENTS_PAGE_SIZE = 50;

export type PartyBankAccountEvent = PartyBankAccountEventFieldsFragment;

export function useBankAccountEvents(bankAccountId: string, { skip = false } = {}) {
  const { data, loading, error, fetchMore, refetch, networkStatus } = useQuery(
    PartyBankAccountEventsDocument,
    {
      variables: { bankAccountId, first: BANK_ACCOUNT_EVENTS_PAGE_SIZE },
      notifyOnNetworkStatusChange: true,
      skip
    }
  );

  const connection = skip ? undefined : data?.partyBankAccountEvents;
  const events: readonly PartyBankAccountEvent[] = connection?.edges.map(edge => edge.node) ?? [];
  const nextCursor = connection?.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;

  const loadMore = useCallback(async () => {
    if (nextCursor) await fetchMore({ variables: { after: nextCursor } });
  }, [fetchMore, nextCursor]);

  /** Reads the history again, e.g. after it failed to load. */
  const retry = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    events,
    hasMore: nextCursor !== null,
    loadMore,
    retry,
    loading: !skip && (loading || networkStatus === NetworkStatus.refetch) && events.length === 0,
    loadingMore: networkStatus === NetworkStatus.fetchMore,
    error: skip ? undefined : error
  };
}

/** The evidence of the latest system signature among the events; null when there is none. */
export function latestSystemEvidence(
  events: readonly PartyBankAccountEvent[]
): SystemCheckEvidence | null {
  const checked = events.filter(
    event => event.eventType === PartyBankAccountEventType.SYSTEM_CHECKED
  );
  const latest = checked[checked.length - 1];
  return latest ? parseSystemCheckEvidence(latest.evidence) : null;
}
